import React, { useState } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { IPaymentDetails } from '@query'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { ReceiverSection } from './ReceiverSection'
import { PaymentDetailsSection } from './PaymentDetailsSection'
import { RemittanceSection } from './RemittanceSection'
import { setAPIErrors } from '@utils'
import { AnimatedContentLoader, Input, Select } from '@components'
import { IHolviAccount, fetchHolviAccounts } from '@query/holvi.ts'
import { getShortAccountIdentifier } from '@root/utils/holvi'
import dayjs from 'dayjs'
import { useBusinessContext, PermissionSet, usePermission } from '@root/context'

interface PaymentFormProps {
  onSuccess: () => void
  defaults?: Partial<IPaymentDetails>
  onSubmitStateChange?: (isSubmitting: boolean) => void
  createPaymentFn: (data: IPaymentDetails) => Promise<IPaymentDetails>
}

export const HolviPaymentForm: React.FC<PaymentFormProps> = ({
  onSuccess,
  defaults,
  onSubmitStateChange,
  createPaymentFn
}) => {
  const { slug } = useBusinessContext()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)

  const {
    data: accountsResponse,
    refetch,
    isLoading: isAccountsLoading
  } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    enabled: Boolean(slug) && canManageIntegrations
  })
  const accounts = accountsResponse?.results || []

  const { t } = useTranslation()
  const [remittanceType, setRemittanceType] = useState<'ref' | 'msg'>(
    // CASE: Default to 'msg' if message is provided AND no reference is provided.
    // Otherwise, default showing reference
    defaults.message && !defaults.reference ? 'msg' : 'ref'
  )

  // Find default Holvi account
  const getDefaultAccountId = (accounts: IHolviAccount[]): number | undefined =>
    accounts
      // Find accounts that have payments and owner
      .filter(
        ({ last_payment_instruction_at, account_owner }) =>
          !!last_payment_instruction_at && !!account_owner
      )
      // Sort by last payment instruction date
      .sort(
        (a, b) => dayjs(b.last_payment_instruction_at).diff(dayjs(a.last_payment_instruction_at))
        // Pick first ID if available
      )?.[0]?.id

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isLoading }
  } = useForm<IPaymentDetails>({
    defaultValues: async () => {
      const { data } = await refetch()
      const accounts = data.results
      return {
        holvi_account: getDefaultAccountId(accounts),
        ...(defaults || {})
      } as IPaymentDetails
    }
  })

  // Update parent component when isSubmitting changes
  React.useEffect(() => {
    onSubmitStateChange?.(isSubmitting)
  }, [isSubmitting, onSubmitStateChange])

  const { mutateAsync: submitPayment } = useMutation({
    mutationFn: (data: IPaymentDetails) => createPaymentFn(data),
    ...{
      onSuccess: async () => {
        toast.success(t('general.success'))
        onSuccess()
      },
      onError: error => {
        const data = (error as { data?: any })?.data
        if (data?.payment_details) {
          setAPIErrors(data?.payment_details, setError)
        }

        if (data?.non_field_errors) {
          toast.error(data?.non_field_errors)
        } else {
          toast.error(t('general.error'))
        }
      }
    }
  })

  const onSubmit = handleSubmit(async data => {
    // Clean up data based on the selected remittance type
    if (remittanceType === 'ref') delete data['message']
    if (remittanceType === 'msg') delete data['reference']
    await submitPayment(data)
  })

  return (
    <AnimatedContentLoader isLoading={isAccountsLoading || isLoading}>
      <StyledForm id="payment-form" onSubmit={onSubmit}>
        <Select label="Holvi Account" {...register('holvi_account')} errors={errors.holvi_account}>
          {accounts.map(account => (
            <option key={account.id} value={account.id}>
              {getShortAccountIdentifier(account, t)}
            </option>
          ))}
        </Select>

        <Spacer />

        <Input
          type="date"
          label={t('containers.paymentFormModal.dueDate')}
          required
          errors={errors.requested_execution_date}
          {...register('requested_execution_date', {
            required: { value: true, message: t('validation.required') }
          })}
        />
        <Spacer />

        <ReceiverSection register={register} errors={errors} />
        <PaymentDetailsSection register={register} errors={errors} />

        <RemittanceSection
          register={register}
          errors={errors}
          type={remittanceType}
          onTypeChange={setRemittanceType}
        />
      </StyledForm>
    </AnimatedContentLoader>
  )
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`

const Spacer = styled.br`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`
